import React, { useCallback, useMemo } from 'react';

import type { InventoryTabItemName } from '../../../InventoryStore';
import { useInventoryState } from '../../../hooks';
import { useRowState } from '../../../hooks/useRowState';
import { isDateAfterEndOfSale, isDatePast } from '../../../utils';
import type { InventoryBadge as InventoryBadgeType } from '../../../utils/badges';
import type { ExperienceOpening } from '../../../utils/experiences';
import type { FieldFormatter } from '../../../utils/fieldFormatters';
import { InventoryBadge } from '../../InventoryBadge/InventoryBadge';
import type {
  InventoryValue,
  StatusInfo,
} from '../../TabViews/inventoryStatusUtils';
import { getStatusInfo as getDefaultStatusInfo } from '../../TabViews/inventoryStatusUtils';
import { isEnabledByMessage } from '../../TabViews/tabViewUtils';
import { LabelCell } from '../Cells/LabelCell/LabelCell';
import type { LabelValueCellProps } from '../Cells/LabelValueCell/LabelValueCell';
import { LabelValueCell } from '../Cells/LabelValueCell/LabelValueCell';
import { PureFieldCell } from '../Cells/PureFieldCell/PureFieldCell';
import { TableRow } from '../TableRow/TableRow';

const getInventoryStatus = (
  values: InventoryValue | undefined
): ExperienceOpening['inventoryStatus'] | undefined => {
  if (!values || !('inventoryStatus' in values)) {
    return undefined;
  }

  return (values as Record<string, unknown>).inventoryStatus as
    | ExperienceOpening['inventoryStatus']
    | undefined;
};

type RowBaseProps = {
  label: string;
  entityId: number;
  fieldName: string;
  displayFieldName?: string;
  entityType: InventoryTabItemName;
  formatter?: FieldFormatter;
  getStatusInfo?: (values: InventoryValue) => StatusInfo | undefined;
  onDuplicate?: (value?: number) => void;
  renderCell?: (values?: InventoryValue) => React.ReactNode;
  cellProps?: Partial<LabelValueCellProps>;
  isEditable?: boolean;
  defaultValue?: number;
  hasStatus?: boolean;
  tooltipTitle?: React.ReactNode;
  hasInfoIcon?: boolean;
  hasSetting?: boolean;
  onClickSettings?: () => void;
  getBadge?: (
    values: InventoryValue,
    date: string
  ) => InventoryBadgeType | null;
};

const RowBaseComponent = React.forwardRef<HTMLDivElement, RowBaseProps>(
  (
    {
      label,
      entityId,
      fieldName,
      displayFieldName = fieldName,
      entityType,
      formatter,
      getStatusInfo,
      onDuplicate,
      renderCell,
      cellProps = {},
      isEditable = true,
      defaultValue = 0,
      hasStatus = false,
      tooltipTitle,
      hasInfoIcon,
      hasSetting,
      onClickSettings,
      getBadge,
    },
    ref
  ) => {
    const { endOfSaleDate } = useInventoryState();

    const { dateKeys } = useRowState(entityType);

    const handleDuplicate = useCallback(
      (value?: number) => onDuplicate?.(value),
      [onDuplicate]
    );

    const memoizedGetStatusInfo = useCallback(
      (values: InventoryValue) => getStatusInfo?.(values),
      [getStatusInfo]
    );

    const cellPropsWithFormatter = useMemo(
      () => ({
        ...(formatter && {
          formatter: formatter.display,
          getValueProps: formatter.getValueProps as
            | ((value: number | string) => Record<string, number | string>)
            | undefined,
          getValueFromEvent: formatter.getValueFromEvent as
            | ((e: number | string) => number)
            | undefined,
          max: formatter.max,
          min: formatter.min,
          precision: formatter.precision,
        }),
        ...cellProps,
      }),
      [formatter, cellProps]
    );

    return (
      <TableRow ref={ref}>
        {hasInfoIcon && tooltipTitle ? (
          hasSetting && onClickSettings ? (
            <LabelCell
              label={label}
              hasInfoIcon
              tooltipTitle={tooltipTitle}
              hasSettings
              onSettingClick={onClickSettings}
            />
          ) : (
            <LabelCell label={label} hasInfoIcon tooltipTitle={tooltipTitle} />
          )
        ) : hasSetting && onClickSettings ? (
          <LabelCell
            label={label}
            hasSettings
            onSettingClick={onClickSettings}
          />
        ) : (
          <LabelCell label={label} />
        )}
        {dateKeys.map((dateKey) => {
          const fieldNameArray = [dateKey, `${entityId}`];
          const fieldValueNameArray = [...fieldNameArray, displayFieldName];

          return (
            <PureFieldCell
              key={dateKey}
              name={fieldNameArray}
              dateKey={dateKey}
            >
              {(values?: InventoryValue) => {
                if (renderCell && values) {
                  return renderCell(values);
                }

                const value = values
                  ? Number(values?.[fieldName as keyof InventoryValue])
                  : defaultValue;

                const statusInfo = values
                  ? memoizedGetStatusInfo(values)
                  : undefined;

                const isPast = isDatePast(dateKey);
                const isAfterEndOfSale = isDateAfterEndOfSale(
                  dateKey,
                  endOfSaleDate
                );

                const isGrayed = isPast || isAfterEndOfSale;
                const isDisabled =
                  isPast || (!!values && !isEnabledByMessage(values?.message));

                const cellInfoStatus = values
                  ? getDefaultStatusInfo(values)
                  : undefined;

                const {
                  isEditable: _,
                  onDuplicate: __,
                  ...safeFormatterProps
                } = cellPropsWithFormatter;

                const badge =
                  values && getBadge ? getBadge(values, dateKey) : undefined;

                const cellLabel = badge ? (
                  <InventoryBadge badge={badge} />
                ) : undefined;

                const baseProps = {
                  value,
                  label: cellLabel,
                  labelPosition: badge ? ('bottom' as const) : undefined,
                  name: fieldValueNameArray,
                  date: dateKey,
                  disabled: isDisabled,
                  grayed: isGrayed,
                  ...(hasStatus
                    ? {
                        status: values ? getInventoryStatus(values) : undefined,
                        statusLabel: statusInfo?.label,
                      }
                    : {}),
                  className: hasStatus
                    ? cellInfoStatus?.cellClassName
                    : undefined,
                  ...safeFormatterProps,
                };

                if (isEditable && onDuplicate) {
                  return (
                    <LabelValueCell
                      {...baseProps}
                      isEditable
                      onDuplicate={() => handleDuplicate(value)}
                    />
                  );
                }

                return <LabelValueCell {...baseProps} />;
              }}
            </PureFieldCell>
          );
        })}
      </TableRow>
    );
  }
);

RowBaseComponent.displayName = 'RowBase';

export const RowBase = React.memo(RowBaseComponent);
